<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-xl font-bold text-slate-800">SMS</h3>
    </div>

    <section class="overflow-hidden rounded-lg ">
      <div class="grid grid-cols-4 gap-4 px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
        <span>Phone Number</span>
        <span>Date</span>
        <span>User Sharing</span>
        <span class="text-right">Action</span>
      </div>

      <div class="divide-y divide-slate-100">
        <div v-if="loading" class="px-6 py-8 text-center text-sm text-slate-500">
          Loading phone numbers...
        </div>
        <div v-else-if="error" class="px-6 py-8 text-center text-sm text-red-500">
          {{ error }}
        </div>
        <div v-else-if="numbers.length === 0" class="px-6 py-8 text-center text-sm text-slate-500">
          No phone numbers found.
        </div>
        <div
          v-else
          v-for="entry in numbers"
          :key="entry.id"
          class="relative grid grid-cols-4 items-center gap-4 px-6 py-4 bg-white mb-2 border border-slate-100 rounded-lg"
        >
          <span class="absolute left-0 top-0 h-full w-1 rounded-l bg-blue-400"></span>
          <div class="flex items-center gap-3 text-sm font-semibold text-slate-800">
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 overflow-hidden">
              <img v-if="entry.flag" :src="entry.flag" alt="country flag" class="h-6 w-6 object-contain">
              <span v-else class="text-lg">🏳️</span>
            </span>
            <span>{{ entry.phone }}</span>
          </div>
          <div class="text-sm text-slate-600">
            {{ entry.date }}
          </div>
          <div class="text-sm text-slate-600">
            {{ entry.sharedUsers }}
          </div>
          <div class="text-right">
            <button
              type="button"
              class="text-sm font-semibold text-blue-600 transition hover:text-blue-700 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
              @click="openDrawer(entry)"
            >
              View More
            </button>
          </div>
        </div>
      </div>
    </section>

    <Drawer
      :is-drawer-open="isDrawerOpen"
      title=""
      :is-right-drawer="true"
      drawer-width="w-lg"
      drawer-padding="p-0"
      @close-drawer="closeDrawer"
    >
      <template #main-content>
        <SmsDetail
          v-if="selectedSms"
          :sms="selectedSms"
          :loading="detailLoading"
          :tenant-id="tenantId"
          @close="closeDrawer"
          @save-recharge="handleSaveRecharge"
        />
      </template>
    </Drawer>
  </div>
</template>

<script>
import Drawer from "../core/Drawer.vue";
import SmsDetail from "../Forms/SmsDetail.vue";
import AnalyticsIntegration from "../../apis/AnalyticsIntegration";

export default {
  name: "SmsTab",
  components: {
    Drawer,
    SmsDetail
  },
  props: {
    tenantId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      numbers: [],
      loading: false,
      error: null,
      isDrawerOpen: false,
      selectedSms: null,
      detailLoading: false
    };
  },
  computed: {
    hasNumbers() {
      return this.numbers.length > 0;
    }
  },
  watch: {
    tenantId: {
      handler(newVal, oldVal) {
        if (!newVal || newVal === oldVal) {
          return;
        }
        this.fetchSmsPhoneNumbers();
      }
    }
  },
  mounted() {
    if (this.tenantId) {
      this.fetchSmsPhoneNumbers();
    }
  },
  methods: {
    getToaster() {
      return this.$toasted || this.$toast || null;
    },
    showToast(type, message) {
      const toaster = this.getToaster();
      if (!toaster) {
        return;
      }
      const handler = toaster[type];
      if (typeof handler === "function") {
        handler(message);
      }
    },
    async fetchSmsPhoneNumbers() {
      this.loading = true;
      this.error = null;
      try {
        const [numbersResponse, logsResponse] = await Promise.all([
          AnalyticsIntegration.getTenantPhoneNumbers(this.tenantId),
          AnalyticsIntegration.getTenantPhoneNumberLogs(this.tenantId)
        ]);

        const numbersPayload = numbersResponse?.data ?? numbersResponse ?? [];
        const logsPayload = logsResponse?.data ?? logsResponse ?? [];
        const logsMap = this.normalizePhoneNumberLogs(logsPayload);

        this.numbers = this.normalizePhoneNumbers(numbersPayload, logsMap);
      } catch (error) {
        this.error = "Failed to load SMS phone numbers";
        this.numbers = [];
      } finally {
        this.loading = false;
      }
    },
    normalizePhoneNumbers(payload, logsMap = {}) {
      if (!Array.isArray(payload)) {
        return [];
      }

      return payload.map((item, index) => {
        const logRecord = logsMap[item.id] ?? logsMap[item.phone_number] ?? null;
        const activityLogs = logRecord
          ? [
            {
              id: logRecord.id,
              requestedBy: logRecord.eventType,
              requestedOn: logRecord.recordedAt,
              creditAmount: logRecord.creditAmount,
              addedOn: logRecord.recordedAt,
              addedBy: "-"
            }
          ]
          : [];

        const countryName = (item.country?.country || item.country || "").toString().toLowerCase();
        const isNepalPhone = countryName.includes("nepal") || (item.phone_country_code === "+977");
        const provider = item.provider ?? "";
        const isAakashSMS = provider === "AakashSMS";

        return {
          id: item.id ?? index,
          country: item.country?.country ?? item.country ?? "",
          flag: item.flag_url ?? item.flag ?? "",
          phone: item.phone_number ?? item.phone ?? "",
          date: logRecord?.recordedAt ?? item.created_at ?? item.updated_at ?? "-",
          sharedUsers: this.formatSharedUsers(item.shared_users ?? item.user_count),
          invoices: [],
          provider: provider,
          canRecharge: isAakashSMS && (isNepalPhone || Boolean(item.can_recharge ?? item.is_rechargeable)),
          rechargePhone: item.recharge_phone ?? item.phone_number ?? item.phone ?? "",
          rechargeCredit: item.recharge_credit ?? "",
          rechargeAddedBy: item.recharge_added_by ?? "",
          activityLogs,
          phoneNumberCode: item.phone_number_code ?? item.phone_code ?? item.phone_number ?? item.phone ?? "",
          isNepali: isNepalPhone,
          creditBalance: item.credit_balance ?? ""
        };
      });
    },
    normalizePhoneNumberLogs(payload) {
      if (!Array.isArray(payload)) {
        return {};
      }

      return payload.reduce((acc, item, index) => {
        const key = item.id ?? item.phone_number ?? index;
        acc[key] = {
          id: item.id ?? `${key}-log`,
          phoneNumber: item.phone_number ?? "",
          eventType: item.event_type ?? "-",
          recordedAt: this.formatDate(item.recorded_at),
          creditAmount: item.credit_amount ?? ""
        };
        return acc;
      }, {});
    },
    formatSharedUsers(value) {
      if (!value) {
        return "-";
      }
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return `${numeric} ${numeric === 1 ? "User" : "Users"}`;
      }
      return value;
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleString();
    },
    openDrawer(entry) {
      this.selectedSms = { ...entry };
      this.isDrawerOpen = true;
      this.fetchSmsDetails(entry);
    },
    closeDrawer() {
      this.isDrawerOpen = false;
      setTimeout(() => {
        this.selectedSms = null;
      }, 200);
    },
    async fetchSmsDetails(entry) {
      if (!this.tenantId) {
        return;
      }

      const phoneCode = entry.phoneNumberCode || entry.phone || entry.id;
      if (!phoneCode) {
        return;
      }

      this.detailLoading = true;
      try {
        const [creditResponse, invoicesResponse] = await Promise.all([
          AnalyticsIntegration.getSmsCreditLogs(this.tenantId, phoneCode),
          AnalyticsIntegration.getTenantPhoneNumberLogDetails(this.tenantId, phoneCode)
        ]);

        const creditPayload = creditResponse?.data ?? creditResponse ?? [];
        const invoicePayload = invoicesResponse?.data ?? invoicesResponse ?? [];

        const invoices = this.normalizeInvoices(invoicePayload);
        const creditLogs = this.normalizeCreditLogs(creditPayload);

        this.selectedSms = {
          ...entry,
          invoices,
          activityLogs: creditLogs
        };
      } catch (error) {
        this.showToast("error", "Failed to load SMS details");
      } finally {
        this.detailLoading = false;
      }
    },
    normalizeInvoices(payload) {
      if (!Array.isArray(payload)) {
        return [];
      }

      return payload.map((item, index) => {
        const payloadData = item.payload?.payload;
        const conversation = payloadData?.conversation;
        const user = conversation?.user;
        
        const creditAddedBy = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : (user?.name ?? item.added_by ?? "-");
        const creditAmount = payloadData?.credit_amount ?? item.credit_amount ?? "-";
        const creditBalance = payloadData?.credit_balance ?? item.credit_balance ?? "-";
        const messageDescription = conversation?.description ?? item.description ?? "-";
        
        return {
          id: item.id ?? `invoice-${index}`,
          date: this.formatDate(item.recorded_at ?? item.date),
          description: this.formatInvoiceDescription(item.event_type ?? item.description ?? "-"),
          paymentMethod: payloadData?.currency_code ?? item.payment_method ?? item.method ?? "NPR",
          totalCost: creditAmount,
          creditBalance: creditBalance,
          creditAddedBy: creditAddedBy,
          messageDescription: messageDescription,
          phoneNumber: item.phone_number ?? "-"
        };
      });
    },
    normalizeCreditLogs(payload) {
      if (!Array.isArray(payload)) {
        return [];
      }

      return payload.map((item, index) => ({
        id: item.id ?? `credit-${index}`,
        requestedBy: this.formatContact(item.credit_requested_by) || item.requested_by || item.user || "-",
        requestedOn: this.formatDate(item.requested_on ?? item.requested_at),
        creditAmount: item.credit_amount ?? item.amount ?? "-",
        addedOn: this.formatDate(item.added_on ?? item.created_at ?? item.recorded_at),
        addedBy: item.added_by ?? item.created_by ?? "-"
      }));
    },
    formatInvoiceDescription(value) {
      if (!value || typeof value !== "string") {
        return value ?? "-";
      }

      const segments = value.split(".").filter(Boolean);
      const candidate = segments.length > 0 ? segments[segments.length - 1] : value;
      const words = candidate
        .split(/[-_]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());

      const label = words.join(" ");
      return label || value;
    },
    formatContact(value) {
      if (!value) {
        return "";
      }
      if (typeof value === "string") {
        return value;
      }
      if (typeof value === "object") {
        const name = value.name || value.full_name || "";
        const email = value.email || "";
        if (name && email) {
          return `${name} (${email})`;
        }
        return name || email;
      }
      return "";
    },
    async handleSaveRecharge(payload) {
      if (!payload.tenant_id || !payload.credit) {
        this.showToast("error", "Please enter credit amount");
        return;
      }

      try {
        await AnalyticsIntegration.addSmsCredit(payload.tenant_id, {
          phone_number_code: payload.phone_number_code,
          credit: payload.credit,
          credit_added_by: payload.credit_added_by
        });
        this.showToast("success", "Credit added successfully");
        this.closeDrawer();
        await this.fetchSmsPhoneNumbers();
      } catch (error) {
        this.showToast("error", "Failed to add credit");
      }
    },
    async handleAddCredit({ phoneNumber, creditAmount, note }) {
      if (!this.tenantId) {
        return;
      }

      const payload = {
        phone_number: phoneNumber,
        credit_amount: creditAmount,
        note: note ?? ""
      };

      try {
        await AnalyticsIntegration.addSmsCredit(this.tenantId, payload);
        this.showToast("success", "Credit added successfully");
        await this.fetchSmsPhoneNumbers();
      } catch (error) {
        this.showToast("error", "Failed to add credit");
      }
    }
  }
};
</script>

